import {
    buildCourierPalletLabel, buildCourierPalletZpl, silentPrintZplRaw, silentPrint, browserPrint, getSavedLocalPrinter,
} from "./printAgent";
import { submitNetworkPrintJob, getSavedNetworkPrinter } from "./networkPrint";

// What the 6x4 courier pallet label says, as the BE renders it for a
// closed pallet (dispatchUnload.controller palletView.print).
export type PalletPrint = { barcode: string; sku: string; classifier: string; carrier_name: string; made_at: string | null };

// The signed-in user's name, printed at the foot of the label.
const signedInName = (): string | null => {
    try {
        const raw = window.localStorage.getItem("user");
        const parsed = raw ? JSON.parse(raw) : null;
        return typeof parsed?.name === "string" && parsed.name.trim() ? parsed.name.trim() : null;
    } catch {
        return null;
    }
};

// Print a closed pallet's 6x4 label, signed with the user who printed it.
// Same route as the sale pallet label (pages/pallets.tsx): saved network
// printer → local Zebra (raw ZPL) → local non-Zebra (HTML) → browser
// dialog. Called by print upon close on the handheld, and by Print label
// on Admin › Warehouse › Outbound.
//
// The network job carries the label as raw ZPL: the desk agent (≥ 1.6.0)
// prints it verbatim on its Zebra. Sending html + barcodeValue alone made
// the agent print its GENERIC 65×38 barcode template — bars and value, no
// courier / sort codes — on every network desk (Poland G1–G5, 2026-10-20).
// html + barcodeValue stay in the job for agents too old to read `zpl`.
export const printCourierPalletLabel = async (print: PalletPrint, fallbackMadeAt?: string | null): Promise<boolean> => {
    const opts = {
        barcode: print.barcode, sku: print.sku, classifier: print.classifier,
        madeAt: print.made_at ?? fallbackMadeAt ?? new Date().toISOString(),
        printedBy: signedInName(), printedAt: new Date(),
    };
    const html = buildCourierPalletLabel(opts);
    const zpl = buildCourierPalletZpl(opts);
    const saved = getSavedNetworkPrinter();
    if (saved) {
        const r = await submitNetworkPrintJob(saved.agentId, saved.printerName, html, 1, print.barcode, false, { zpl });
        if (r.success) return true;
    }
    const zebra = await silentPrintZplRaw(zpl, 1, getSavedLocalPrinter() ?? undefined);
    if (zebra.success) return true;
    const local = await silentPrint(html, 1, getSavedLocalPrinter() ?? undefined);
    if (local.success) return true;
    const browser = await browserPrint(html);
    return browser.success;
};
